import { h as __h } from "preact";
/** @import { ComponentChildren } from "preact"; */

import html from "~/util/html.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./PasswordRules.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./PasswordRules.module.css", import.meta.url);
const css = adopt(sheet);
const MIN_LENGTH = 12;
const MAX_LENGTH = 64;
const UPPERCASE = /[A-Z]/;
const LOWERCASE = /[a-z]/;
const DIGIT = /[0-9]/;
const SYMBOL = /[!@#$%^&*\(\)_+\-=\[\]\{\};':"\|,. <>\/\\?]/; // oxlint-disable-line no-useless-escape (need this for serializing to string)

/**
 * Compile multiple regular expressions into an <input> pattern
 *
 * @param {RegExp[]} exps
 */
function pattern(...exps) {
  const contents = exps.map(re => re.toString().slice(1, -1));
  const lookaheads = contents.map(re => `(?=.*${re})`);
  return `${lookaheads.join("")}(${contents.join("|")})+`;
}

/** Rules for use in <input type="password"> attributes. */
export const RULES = {
  minlength: MIN_LENGTH,
  maxlength: MAX_LENGTH,
  pattern: pattern(UPPERCASE, LOWERCASE, DIGIT, SYMBOL)
};

/**
 * @typedef {object} Props
 * @property {string} password
 * @property {ComponentChildren} [children]
 */

/** @param {Props} props */
export default function PasswordRules({
  children,
  password
}) {
  const validLength = MIN_LENGTH <= password.length && password.length <= MAX_LENGTH;
  const hasUpper = UPPERCASE.test(password);
  const hasLower = LOWERCASE.test(password);
  const hasDigit = DIGIT.test(password);
  const hasSymbol = SYMBOL.test(password);
  const progress = Number(validLength) + Number(hasUpper) + Number(hasLower) + Number(hasDigit) + Number(hasSymbol);
  return __h("div", {
    "class": css.wrapper
  }, children, __h("ul", {
    "class": css.list,
    role: "progressbar",
    "aria-label": true,
    "aria-valuemin": "0",
    "aria-valuemax": "5",
    "aria-valuenow": progress
  }, __h("li", {
    "class": css.rule,
    "data-valid": validLength
  }, MIN_LENGTH, " characters"), __h("li", {
    "class": css.rule,
    "data-valid": hasUpper
  }, "Uppercase letter"), __h("li", {
    "class": css.rule,
    "data-valid": hasLower
  }, "Lowercase letter"), __h("li", {
    "class": css.rule,
    "data-valid": hasDigit
  }, "Number"), __h("li", {
    "class": css.rule,
    "data-valid": hasSymbol
  }, "Symbol")));
}